import { useState } from "react";
import { ChevronRight, ChevronDown } from "lucide-react";

// Technical identifiers — shown but locked.
const LOCKED_KEYS = new Set(["key", "id", "icon"]);

function labelize(key) {
  return String(key)
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase());
}

function matches(node, filter) {
  if (!filter) return true;

  const f = filter.toLowerCase();

  if (typeof node === "string") {
    return node.toLowerCase().includes(f);
  }

  if (Array.isArray(node)) {
    return node.some((v) => matches(v, filter));
  }

  if (node && typeof node === "object") {
    return Object.entries(node).some(
      ([k, v]) =>
        k.toLowerCase().includes(f) || matches(v, filter)
    );
  }

  return false;
}

function StringField({ label, value, locked, onChange }) {
  const long = (value || "").length > 60;

  return (
    <div className="mb-3">
      <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">
        {label}
      </label>

      {long ? (
        <textarea
          value={value}
          disabled={locked}
          onChange={(e) => onChange(e.target.value)}
          rows={Math.min(6, Math.ceil(value.length / 60) + 1)}
          className="w-full bg-[#0A0F2E] border border-white/10 focus:border-[#00F0FF]/50 rounded px-3 py-2 text-sm text-slate-100 outline-none disabled:opacity-40"
        />
      ) : (
        <input
          type="text"
          value={value}
          disabled={locked}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-[#0A0F2E] border border-white/10 focus:border-[#00F0FF]/50 rounded px-3 py-2 text-sm text-slate-100 outline-none disabled:opacity-40"
        />
      )}
    </div>
  );
}

function Section({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="mb-4 border border-white/10 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-3 bg-[#0A0F2E] hover:bg-[#0d1440] text-left"
      >
        {open ? (
          <ChevronDown
            size={16}
            className="text-[#00F0FF]"
          />
        ) : (
          <ChevronRight
            size={16}
            className="text-slate-500"
          />
        )}

        <span className="text-sm font-semibold text-white">
          {title}
        </span>
      </button>

      {open && <div className="p-4">{children}</div>}
    </div>
  );
}

function renderNode(node, path, label, filter, onChange) {
  if (!matches(node, filter)) {
    return null;
  }

  const key = path.length
    ? path[path.length - 1]
    : label;

  // String leaf
  if (typeof node === "string") {
    const locked = LOCKED_KEYS.has(String(key));

    return (
      <StringField
        label={labelize(key)}
        value={node}
        locked={locked}
        onChange={(value) => onChange(path, value)}
      />
    );
  }

  // Non-text leaf
  if (typeof node === "number" || typeof node === "boolean") {
    return null;
  }

  if (!node || typeof node !== "object") {
    return null;
  }

  const entries = Array.isArray(node)
    ? node.map((value, index) => [index, value])
    : Object.entries(node);

  return entries.map(([key, value]) => (
    <div key={String(key)}>
      {renderNode(
        value,
        [...path, key],
        labelize(key),
        filter,
        onChange
      )}
    </div>
  ));
}

export function ContentNode({
  node,
  path,
  label,
  filter,
  onChange,
  topLevel = false,
}) {
  if (!matches(node, filter)) {
    return null;
  }

  if (topLevel) {
    return (
      <Section title={label} defaultOpen={!!filter}>
        {renderNode(node, path, label, filter, onChange)}
      </Section>
    );
  }

  return (
    <div className="mb-3 pl-3 border-l border-white/10">
      <div className="text-[11px] uppercase tracking-wide text-[#00F0FF]/70 mb-2">
        {label}
      </div>

      {renderNode(node, path, label, filter, onChange)}
    </div>
  );
}